/**
 * PickupTypes - Definitions for all pickup types in the game
 *
 * This file defines the standard pickup types and provides registration
 * functions to populate the PickupRegistry.
 *
 * @example
 * import { registerDefaultPickupTypes, WAVE_PICKUP } from './pickups/PickupTypes.js';
 * import { pickupRegistry } from '../../registries/PickupRegistry.js';
 *
 * // Register all default types
 * registerDefaultPickupTypes(pickupRegistry);
 *
 * // Or use the WAVE_PICKUP definition directly
 * console.log(WAVE_PICKUP.signLabel); // 'NEXT LOT'
 */

import { eventBus, GameEvents } from '../../utils/EventBus.js';
import { SALVAGE_CRATE_BALANCE } from '../../config/balance.js';

/**
 * @typedef {Object} PickupTypeConfig
 * @property {string} id - Unique identifier (e.g., 'wave', 'salvage-crate')
 * @property {number} weight - Spawn probability weight (higher = more common, e.g., 0.75 for 75%)
 * @property {string} signLabel - Road sign display text (max 20 chars, e.g., 'NEXT LOT')
 * @property {string|null} model - 3D model path, or null to use type-specific default
 * @property {number|null} particleColor - Collection effect color (hex), or null for dynamic (quality-based)
 * @property {boolean} autoCollect - If true, execute callback immediately; if false, goes to inventory
 * @property {function(Object, Object): void} [onCollect] - Collection callback (gameState, pickupData)
 */

/**
 * Wave Pickup Type
 *
 * Waves appear on the road and contain item packs. When collected,
 * they're added to the player's available packs for later opening.
 *
 * Behavior:
 * - autoCollect: false (goes to inventory, opened via LOTS menu)
 * - particleColor: null (determined dynamically by pack quality)
 * - onCollect: Adds to state.waves.availablePacks, emits WAVE_COLLECTED event
 *
 * @type {PickupTypeConfig}
 */
export const WAVE_PICKUP = {
    id: 'wave',
    weight: 0.75,
    signLabel: 'NEXT LOT',
    model: null, // Uses existing cassette model in WavePickupManager
    particleColor: null, // Dynamic based on pack quality
    autoCollect: false,

    /**
     * Handle wave collection
     * @param {Object} gameState - The GameState instance
     * @param {Object} pickupData - Data about the collected pickup
     * @param {number} pickupData.milestone - Mile marker where collected
     * @param {number} pickupData.packQuality - Quality value (0.0 to 1.0)
     * @param {number} pickupData.cost - Mile cost to open this pack
     * @param {boolean} [pickupData.isTutorial] - Whether this is the tutorial pack
     */
    onCollect: (gameState, pickupData) => {
        // Add pack to available packs
        const waves = gameState.state.waves;
        if (!waves.availablePacks) {
            waves.availablePacks = [];
        }

        // Create pack entry matching existing structure
        const pack = {
            milestone: pickupData.milestone,
            packQuality: pickupData.packQuality,
            cost: pickupData.cost,
            collectedAt: Date.now(),
            opened: false,
            storedItems: null,
            seen: false,
            slotType: pickupData.slotType || null  // null for generic, string for type-specific
        };

        // Mark tutorial as shown if applicable
        if (pickupData.isTutorial) {
            waves.tutorialShown = true;
            pack.isTutorial = true;
        }

        waves.availablePacks.push(pack);

        // Emit collection event
        eventBus.emit(GameEvents.MILESTONE_REACHED, {
            milestone: pickupData.milestone,
            packsAvailable: waves.availablePacks.length,
            packQuality: pickupData.packQuality
        });

        // Save state
        gameState.save();
    }
};

/**
 * Salvage Crate Pickup Type
 *
 * Salvage crates appear on the road and instantly award wavedust when collected.
 * They ensure players always have wavedust flowing to fund lot opening.
 *
 * Behavior:
 * - autoCollect: true (instant pickup, no inventory)
 * - particleColor: 0x44aa88 (teal/green - distinct from quality colors)
 * - onCollect: Awards wavedust based on quality, returns data for popup
 *
 * @type {PickupTypeConfig}
 */
export const SALVAGE_CRATE_PICKUP = {
    id: 'salvage-crate',
    weight: 0.25,
    signLabel: 'SALVAGE',
    model: null, // Will use box/crate geometry in WavePickupManager
    particleColor: 0x44aa88, // Teal/green - distinct from quality colors
    autoCollect: true,

    /**
     * Handle salvage crate collection
     * @param {Object} gameState - The GameState instance
     * @param {Object} pickupData - Data about the collected pickup
     * @param {number} pickupData.packQuality - Quality value (0.0 to 1.0)
     * @returns {Object} Data for popup display { wavedust, bonusPercent }
     */
    onCollect: (gameState, pickupData) => {
        const quality = pickupData.packQuality || 0.5;

        // Calculate base wavedust with quality scaling
        const baseAmount = SALVAGE_CRATE_BALANCE.BASE_WAVEDUST;
        const qualityMultiplier = 1 + (quality * SALVAGE_CRATE_BALANCE.QUALITY_SCALE);

        // Apply random variance (±20%)
        const variance = 1 + ((Math.random() * 2 - 1) * SALVAGE_CRATE_BALANCE.VARIANCE);

        let wavedust = Math.round(baseAmount * qualityMultiplier * variance);

        // Apply scrap bonus skill if active (reuses existing skill)
        const scrapBonus = gameState?.getScrapBonus?.() || 0;
        let bonusPercent = 0;
        if (scrapBonus > 0) {
            bonusPercent = scrapBonus * 100;
            wavedust = Math.round(wavedust * (1 + scrapBonus));
        }

        // Award wavedust to player
        gameState.addWavedust(wavedust);

        // Save state
        gameState.save();

        // Return data for popup display
        return { wavedust, bonusPercent };
    }
};

/**
 * Register all default pickup types with a registry
 *
 * @param {PickupRegistry} registry - The pickup registry to populate
 * @returns {void}
 */
export function registerDefaultPickupTypes(registry) {
    // Only register if not already present (idempotent)
    if (!registry.has('wave')) {
        registry.register(WAVE_PICKUP.id, WAVE_PICKUP);
    }
    if (!registry.has('salvage-crate')) {
        registry.register(SALVAGE_CRATE_PICKUP.id, SALVAGE_CRATE_PICKUP);
    }
}

/**
 * Get the particle color for a pickup based on quality
 * Used when pickupType.particleColor is null (dynamic)
 *
 * @param {number} quality - Pack quality value (0.0 to 1.0)
 * @returns {number} Hex color value
 */
export function getQualityParticleColor(quality) {
    if (quality < 0.2) return 0x888888;  // faded - gray
    if (quality < 0.4) return 0x4a9eff;  // standard - blue
    if (quality < 0.6) return 0xa335ee;  // pristine - purple
    if (quality < 0.8) return 0xff8000;  // golden - orange
    return 0xe6cc80;                      // vectorized - gold
}
